import React, { useRef, useState } from 'react';

const GalleryUpload = ({ onFilesSelect, accept, maxSize, maxFiles, label, className = '', resetTrigger = 0 }) => {
  const fileInputRef = useRef(null);
  const onFilesSelectRef = useRef(onFilesSelect);
  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);

  // Update ref when onFilesSelect changes
  React.useEffect(() => {
    onFilesSelectRef.current = onFilesSelect;
  }, [onFilesSelect]);

  // Reset gallery when resetTrigger changes
  React.useEffect(() => {
    if (resetTrigger > 0) {
      setSelectedFiles([]);
      onFilesSelectRef.current([]);
    }
  }, [resetTrigger]);

  const handleFilesSelect = (files) => {
    const validFiles = Array.from(files).filter(file => {
      if (file.size > maxSize) {
        alert(`File ${file.name} is too large. Max size is ${Math.round(maxSize / (1024 * 1024))}MB`);
        return false;
      }
      return true;
    });

    const newFiles = [...selectedFiles, ...validFiles].slice(0, maxFiles);
    setSelectedFiles(newFiles);
    onFilesSelectRef.current(newFiles);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFilesSelect(files);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (e) => {
    const files = e.target.files;
    if (files.length > 0) {
      handleFilesSelect(files);
    }
  };

  const removeFile = (index) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(newFiles);
    onFilesSelectRef.current(newFiles);
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleItemDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleItemDragEnter = (e, index) => {
    e.preventDefault();
    e.currentTarget.classList.add('border-primary-400', 'bg-primary-50');
  };

  const handleItemDragLeave = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove('border-primary-400', 'bg-primary-50');
  };

  const handleItemDrop = (e, dropIndex) => {
    e.preventDefault();
    e.currentTarget.classList.remove('border-primary-400', 'bg-primary-50');
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    const newFiles = [...selectedFiles];
    const draggedFile = newFiles[draggedIndex];
    
    // Remove dragged file from original position
    newFiles.splice(draggedIndex, 1);
    
    // Insert at new position
    const newIndex = draggedIndex < dropIndex ? dropIndex - 1 : dropIndex;
    newFiles.splice(newIndex, 0, draggedFile);
    
    setSelectedFiles(newFiles);
    onFilesSelectRef.current(newFiles);
    setDraggedIndex(null);
  };

  return (
    <div className={className}>
      <div
        className={`file-upload-area rounded-lg p-6 text-center cursor-pointer ${isDragOver ? 'dragover' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          multiple
          onChange={handleFileInputChange}
          className="hidden"
        />
        
        <div className="space-y-2">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            stroke="currentColor"
            fill="none"
            viewBox="0 0 48 48"
          >
            <path
              d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          
          <div className="text-sm text-gray-600">
            <span className="font-medium text-primary-600 hover:text-primary-500">
              Click to upload
            </span>
            {' '}or drag and drop
          </div>
          
          <p className="text-xs text-gray-500">
            {label} (Max {maxFiles} files, {Math.round(maxSize / (1024 * 1024))}MB each)
          </p>
          
          {selectedFiles.length > 0 && (
            <p className="text-xs text-primary-600">
              {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''} selected
            </p>
          )}
        </div>
      </div>

      {/* Gallery Preview */}
      {selectedFiles.length > 0 && (
        <div className="mt-4">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Gallery Images
              <span className="text-xs text-gray-500 ml-2 font-normal">(Drag to reorder)</span>
            </label>
            <div className="w-full flex flex-row gap-6 overflow-x-auto">
              {selectedFiles.map((file, index) => (
                <div 
                  key={`${file.name}-${index}`}
                  className={`gallery-item relative group cursor-move w-40 h-40 flex-shrink-0 transition-all duration-300 overflow-hidden ${
                    draggedIndex === index 
                      ? 'opacity-30 scale-110 rotate-2 shadow-2xl z-50' 
                      : 'opacity-100 scale-100 rotate-0'
                  }`}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={handleItemDragOver}
                  onDragEnter={(e) => handleItemDragEnter(e, index)}
                  onDragLeave={handleItemDragLeave}
                  onDrop={(e) => handleItemDrop(e, index)}
                >
                  <img
                    src={URL.createObjectURL(file)}
                    alt={`Gallery item ${index + 1}`}
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                  />
                  
                  {/* Drag handle indicator */}
                  <div className="absolute top-1 left-1 bg-black bg-opacity-80 text-white text-xs px-2 py-1 rounded-md flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M7 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 14a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 14a2 2 0 1 0 0 4 2 2 0 0 0 0-4z"/>
                    </svg>
                    <span className="text-xs">Drag</span>
                  </div>
                  
                  {/* Remove button */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFile(index);
                    }}
                    className="absolute bottom-1 right-1 bg-red-500 hover:bg-red-600 text-white w-6 h-6 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                    title="Remove image"
                  >
                    <svg
                      className="w-3 h-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryUpload;
